import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { DatastoreService } from './datastore.service';

export type CeremonyAlert =
	| 'addon'
	| 'extended'
	| 'cancelled'
	| 'rescheduled'
	| 'choices';

// `defaultTo` is the recipient to use when the config names none. The
// RWRSPI-1055 alerts deliberately omit it so they keep landing in the
// BookingLab inbox until the council signs each one off; PD-815 was specified
// against the council address, so it carries its own.
const ALERTS: Record<
	CeremonyAlert,
	{ subject: string; templateKey: string; defaultTo?: string; alsoTo?: string }
> = {
	// `alsoTo` is appended to the recipient (JRNI accepts a `;`-separated
	// recipient list) so Chad can monitor add-on transactions.
	addon: {
		subject: 'Add on purchase ALERT',
		templateKey: 'addon_alert',
		alsoTo: 'chad.duggan@bookinglab.co.uk',
	},
	extended: { subject: 'Extended ceremony ALERT', templateKey: 'extended_ceremony_alert' },
	cancelled: { subject: 'Cancelled Ceremony ALERT', templateKey: 'cancelled_ceremony_alert' },
	rescheduled: { subject: 'Rescheduled Ceremony ALERT', templateKey: 'rescheduled_ceremony_alert' },
	choices: {
		subject: 'Customer ceremony choices have been submitted',
		templateKey: 'ceremony_choices_locked',
		defaultTo: 'registeroffice@westminster.gov.uk',
	},
};

// Alerts the register office when a customer completes a self-service action
// (RWRSPI-1055/1056/1058/1059, PD-815). Best-effort: never blocks the customer.
@Injectable({ providedIn: 'root' })
export class AlertNotificationService {
	constructor(
		private apiService: ApiService,
		private datastore: DatastoreService
	) {}

	// `scope` must be unique per operation, not per booking, or a legitimate
	// repeat action is silently suppressed.
	async send(
		alert: CeremonyAlert,
		booking: { id: number | string; company_id: number },
		scope: string | number
	): Promise<void> {
		if (!this.datastore.alertsEnabled) return;
		if (!booking?.id || !booking?.company_id) return;

		const { subject, templateKey, defaultTo, alsoTo } = ALERTS[alert];
		const primaryTo =
			this.datastore.registerOfficeEmailConfigured ??
			defaultTo ??
			DatastoreService.DEFAULT_REGISTER_OFFICE_EMAIL;
		const to = alsoTo ? `${primaryTo};${alsoTo}` : primaryTo;
		// Refresh, Back button, overlapping payment polls and double-clicked
		// buttons all replay the call sites, so dedupe here.
		const marker = `wcc_alert_sent:${templateKey}:${scope}`;
		if (localStorage.getItem(marker)) return;
		localStorage.setItem(marker, '1');

		try {
			await this.apiService.sendBackOfficeAlertEmail(
				booking.company_id,
				booking.id,
				to,
				this.datastore.notificationsFromEmail,
				subject,
				templateKey
			);
		} catch (err) {
			localStorage.removeItem(marker);
			console.error(`[alert-email] failed to send ${alert} alert`, err);
			(window as any).newrelic?.noticeError(err, { alert, bookingId: booking.id });
		}
	}
}
